import React from "react";
import { Target, Eye, ShieldCheck, Users, CheckCircle2, Building2, Landmark, CalendarDays } from "lucide-react";
import TiltCard from "../components/TiltCard";
import PageBanner, { SectionTitle } from "../components/PageBanner";
import HQGallery from "../components/HQGallery";
import { boardMembers, objectives, milestones, pageBanners } from "../data/mock";

export default function About() {
  const pillars = [
    { icon: Eye, title: "Vision", text: "Affordable, world-class healthcare and medical education for every citizen of Andhra Pradesh — a Government medical college in every district.", tone: "" },
    { icon: Target, title: "Mission", text: "Mobilise resources and build modern medical colleges, nursing colleges, super-speciality hospitals and cancer centres with transparent, time-bound execution.", tone: "icon-tile-yellow" },
    { icon: ShieldCheck, title: "Mandate", text: "Act as the holding entity for institutional assets and revenues while colleges and hospitals continue day-to-day management under existing administration.", tone: "icon-tile-red" },
  ];
  const facts = [
    { icon: CalendarDays, k: "Incorporated", v: "24 February 2021" },
    { icon: Landmark, k: "Statute", v: "APMERC Act, 2021 (Act No. 7 of 2021)" },
    { icon: Building2, k: "Registered Office", v: "Phy Care Building, IT Park, Guntur" },
    { icon: Users, k: "Governance", v: "Board chaired by Spl. Chief Secretary, HM&FW" },
  ];

  return (
    <div className="page-enter">
      <PageBanner image={pageBanners.about} title="About Us" subtitle="Learn about the Corporation's role in strengthening medical education and public healthcare across Andhra Pradesh." />

      <section style={{ padding: "50px 24px 30px" }}>
        <div style={{ maxWidth: 1300, margin: "0 auto", display: "grid", gridTemplateColumns: "1.25fr 1fr", gap: 34, alignItems: "start" }} className="two-col">
          <div>
            <SectionTitle>The Corporation</SectionTitle>
            <p style={{ color: "#1C1917", fontSize: 15.5, lineHeight: 1.9, textAlign: "justify" }}>
              The Andhra Pradesh Medical Education and Research Corporation Limited (APMERCL) is a public limited company incorporated on 24 February 2021 under the Companies Act, 2013 (CIN U74999AP2021SGC117458). It functions as a State Government enterprise dedicated to the revitalisation of tertiary healthcare and the strengthening of medical education infrastructure across Andhra Pradesh.
            </p>
            <blockquote data-testid="act-callout" style={{ margin: "20px 0 0", padding: "18px 22px", borderLeft: "5px solid #F97316", background: "linear-gradient(90deg,#FFF7ED,#FEFCE8)", borderRadius: 12, color: "#1C1917", fontSize: 15.5, lineHeight: 1.8, fontWeight: 500, boxShadow: "0 8px 20px -14px rgba(194,65,12,0.4)" }}>
              <span style={{ display: "block", fontSize: 11, letterSpacing: 1.4, fontWeight: 800, color: "#EA580C", marginBottom: 6 }}>STATUTORY MANDATE</span>
              The Andhra Pradesh Medical Education and Research Corporation (APMERC) Act, 2021 (Act No. 7 of 2021) establishes APMERC to revitalize tertiary health care, strengthen medical institutions, and build new hospitals and colleges in the state.
            </blockquote>
            <p style={{ color: "#44403C", fontSize: 15.5, lineHeight: 1.9, marginTop: 14, textAlign: "justify" }}>
              The APMERC Bill, 2021 was introduced in the Andhra Pradesh Legislative Assembly on 20 May 2021 and enacted as Act No. 7 of 2021. The Act provides for: (i) the establishment of APMERC for revitalising tertiary hospitals and health-care services, (ii) strengthening of the existing medical institutions, and (iii) construction of new hospitals and medical colleges — ensuring at least one Government medical college in every district.
            </p>
            <p style={{ color: "#44403C", fontSize: 15.5, lineHeight: 1.9, marginTop: 14, textAlign: "justify" }}>
              Fees collected from Competent Authority (self-financing) and NRI category seats in Government medical colleges are deposited with APMERCL to fund the development of existing and new institutions, while the Directorate of Medical Education continues to oversee academic and hospital administration.
            </p>
          </div>
          <div style={{ display: "grid", gap: 12, alignContent: "start" }}>
          <TiltCard intensity={3}>
            <div className="objectives-card" style={{ overflow: "hidden", borderRadius: 20 }}>
              <div style={{ height: 6, background: "linear-gradient(90deg,#F97316,#FACC15)" }} />
              <div style={{ padding: "12px 14px 10px" }}>
                <div className="eyebrow">Objectives</div>
                <ul style={{ listStyle: "none", padding: 0, margin: "6px 0 0", display: "grid", gap: 4 }}>
                  {objectives.map((o, i) => (
                    <li key={i} style={{ display: "flex", gap: 8, fontSize: 13.5, color: "#1C1917", lineHeight: 1.35 }}><CheckCircle2 size={15} color="#F97316" style={{ flexShrink: 0, marginTop: 2 }} /> {o}</li>
                  ))}
                </ul>
              </div>
            </div>
          </TiltCard>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }} data-testid="fact-cards">
              {facts.map(f => (
                <div key={f.k} className="premium-card" data-testid={`fact-${f.k.toLowerCase().replace(/\s+/g, "-")}`} style={{ padding: 16, display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <div className="icon-tile" style={{ width: 42, height: 42, borderRadius: 12, flexShrink: 0 }}><f.icon size={18} /></div>
                  <div style={{ minWidth: 0 }}><div style={{ fontSize: 11, fontWeight: 800, color: "#EA580C", letterSpacing: 1 }}>{f.k.toUpperCase()}</div><div style={{ fontSize: 13.5, fontWeight: 600, color: "#1C1917", marginTop: 4, lineHeight: 1.4 }}>{f.v}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section style={{ padding: "30px 24px 50px" }}>
        <div style={{ maxWidth: 1300, margin: "0 auto" }}>
          <SectionTitle>Vision, Mission &amp; Mandate</SectionTitle>
          <div className="grid-eq grid-3" style={{ display: "grid", gap: 22 }}>
            {pillars.map(p => (
              <TiltCard key={p.title} intensity={5}>
                <div style={{ padding: 30 }}>
                  <div className={`icon-tile ${p.tone}`} style={{ width: 56, height: 56 }}><p.icon size={26} /></div>
                  <h3 className="font-serif" style={{ fontSize: 22, color: "#1C1917", marginTop: 18, fontWeight: 700 }}>{p.title}</h3>
                  <p style={{ color: "#44403C", fontSize: 14.5, lineHeight: 1.7, marginTop: 10 }}>{p.text}</p>
                </div>
              </TiltCard>
            ))}
          </div>
        </div>
      </section>

      <HQGallery />

      <section style={{ padding: "30px 24px 50px" }} className="soft-bg">
        <div style={{ maxWidth: 1300, margin: "0 auto" }}>
          <SectionTitle>Our Journey</SectionTitle>
          <div className="grid-eq grid-6 journey-grid" style={{ position: "relative", display: "grid", gap: 16, alignItems: "stretch" }} data-testid="milestones">
            <div className="journey-line hide-mobile" style={{ position: "absolute", left: 0, right: 0, top: 18, height: 3, background: "linear-gradient(90deg,#F97316,#FACC15,#DC2626)", borderRadius: 2 }} />
            {milestones.map((m, i) => (
              <div key={i} className="journey-item" style={{ position: "relative", display: "flex", flexDirection: "column", height: "100%", paddingTop: 44 }}>
                <div className="journey-dot" style={{ position: "absolute", top: 8, left: 0, width: 22, height: 22, borderRadius: "50%", background: "#fff", border: "5px solid #F97316", boxShadow: "0 0 0 4px rgba(249,115,22,0.2)" }} />
                <div className="premium-card journey-card" style={{ padding: 16, flex: 1, display: "flex", flexDirection: "column", height: "100%" }}>
                  <span className="badge badge-orange" style={{ alignSelf: "flex-start" }}>{m.year}</span>
                  <div className="journey-title" style={{ fontWeight: 700, fontSize: 15, color: "#1C1917", marginTop: 10, lineHeight: 1.3, minHeight: "2.6em" }}>{m.title}</div>
                  <div className="journey-text" style={{ fontSize: 13, color: "#44403C", marginTop: 8, lineHeight: 1.5, flex: 1 }}>{m.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: "40px 24px 60px" }}>
        <div style={{ maxWidth: 1300, margin: "0 auto" }}>
          <SectionTitle>Board of Directors <span style={{ fontSize: 13, fontWeight: 600, opacity: 0.95 }}>· G.O.Ms.No.9 · 02 February 2021</span></SectionTitle>
          <div className="grid-eq grid-3" style={{ display: "grid", gap: 18 }}>
            {boardMembers.map((m, i) => (
              <TiltCard key={i} intensity={3}>
                <div style={{ padding: 22, display: "flex", gap: 14 }}>
                  <div className={`icon-tile ${i === 0 ? "icon-tile-red" : ""}`} style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0 }}><Users size={20} /></div>
                  <div>
                    <div style={{ fontSize: 11, color: "#EA580C", fontWeight: 800, letterSpacing: 1 }}>{m.role.toUpperCase()}</div>
                    <div style={{ color: "#1C1917", fontSize: 13.5, marginTop: 4, lineHeight: 1.5 }}>{m.title}</div>
                  </div>
                </div>
              </TiltCard>
            ))}
          </div>
        </div>
      </section>
      <style>{`
        @media (max-width: 1000px) {
          .two-col { grid-template-columns: 1fr !important; }
          .hide-mobile, .journey-line, .journey-dot { display: none !important; }
          .journey-item { padding-top: 0 !important; }
        }
        @media (max-width: 640px) {
          [data-testid="fact-cards"] { grid-template-columns: 1fr !important; }
        }
        .grid-eq .journey-card > .journey-title { flex: 0 0 auto; }
        .grid-eq .journey-card > .journey-text { flex: 1 1 auto; margin-top: 8px; }
        @media (max-width: 1200px) {
          .journey-line, .journey-dot { display: none !important; }
          .journey-item { padding-top: 0 !important; }
        }
        @media (max-width: 640px) {
          .journey-grid { grid-template-columns: 1fr !important; }
          .journey-title { min-height: 0 !important; }
        }
      `}</style>
    </div>
  );
}
